import React, { Component } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { RouteComponentProps } from 'react-router'
import { AppState, Dispatch } from 'store/types'
import {
  fetchRelationships,
  fetchGenders,
  fetchAppointments,
  fetchPatients,
  fetchPhysicians,
  fetchCpt4Codes
} from 'ducks/references/operations'
import { fecthEstimateById, createEstimate } from 'ducks/estimates/operations'
import EstimateForm from 'components/estimates/EstimateForm'
import { handleErrorMessageAlert } from 'components/UI/Alert'
import { camelCaseToSentence } from 'utils/helpers'
import { routePath, history } from 'routes'

interface UserInfo {
  dob?: string
  firstName?: string
  gender?: string
  lastName?: string
  middleInitial?: string
}
interface Form extends UserInfo {
  payer?: string
  memberId?: string
  dependent?: UserInfo
  relationship?: any
  appointmentIdSet?: any
  cpt4IdSet?: any
  gender?: any
}

interface IFormState {
  form?: Form
  isFetched: boolean
  errors: any
}

class EstimateCreatePage extends Component<Props, IFormState> {
  constructor(props: Props) {
    super(props)

    this.state = {
      form: {},
      isFetched: false,
      errors: {}
    }
  }

  componentDidMount(): void {
    const { match: { params: { id } } } = this.props
    Promise.all(
      [
        this.props.fecthEstimateById(id),
        this.props.fetchRelationships(),
        this.props.fetchGenders(),
        this.props.fetchAppointments(),
        this.props.fetchPatients(),
        this.props.fetchPhysicians(),
        this.props.fetchCpt4Codes()
      ]
    )
    .then(() => {
      this.setState({ isFetched: true })
    })
  }

  componentDidUpdate(prevProps: Props, prevState: IFormState) {
    if (
      !prevState.isFetched && this.state.isFetched
    ) {
      const estimate: any = { ...this.props.estimate }
      const form: any = { ...this.props.estimate }
      form.payer = estimate.payer && findInDictionary(
        this.props.patients,
        estimate.payer.id
      )
      form.dependent = estimate.dependentEntity
      if (form.dependent) {
        form.dependent.gender = estimate.dependentEntity && findInDictionary(
          this.props.genders,
          estimate.dependentEntity.gender
        )
      }
      form.gender = findInDictionary(
        this.props.genders,
        estimate.gender
      )
      form.relationship = findInDictionary(
        this.props.relationships,
        estimate.relationship
      )
      const appointmentId = estimate.appointmentTypeEntityList &&
        estimate.appointmentTypeEntityList[0] && 
        estimate.appointmentTypeEntityList[0].id
      form.appointmentIdSet = findInDictionary(
        this.props.appointments,
        appointmentId
      )
      const cpt4Id = estimate.cpt4EntitySet &&
        estimate.cpt4EntitySet[0] &&
        estimate.cpt4EntitySet[0].id
      form.cpt4IdSet = findInDictionary(
        this.props.cpt4Codes,
        cpt4Id
      )
      form.physician = findInDictionary(
        this.props.physicians,
        estimate.physicianId
      )
      delete form.dependentEntity
      this.setState({ form })
    }
  }

  onChangeInput = (name: string, value: string | null): void => {
    const nameArr = name.split('.')

    const getValue = (state: any) =>
      nameArr && nameArr.length === 2
        ? {
            [nameArr[0]]: {
              ...state[nameArr[0]],
              [nameArr[1]]: value
            }
          }
        : { [nameArr[0]]: value }

    this.setState(state => {
      const newState = {
        ...state,
        form: {
          ...state.form,
          ...getValue(state.form)
        },
        errors: {}
      }
      return newState
    })
  }

  validate = () => {
    const stateForm: any = { ...this.state.form }
    const errors: any = {}
    const { dob, dependent = {} } = stateForm
    if (dob) {
      const [ month, day, year ] = dob.split('/')
      if (+month > 12 || +day > 31 || year.length < 4) {
        errors.dob = 'Invalid subscriber Date of birth format'
      }
    } 
    if (dependent && dependent.dob) {
      const [ month, day, year ] = dependent.dob.split('/')
      if (+month > 12 || +day > 31 || year.length < 4) {
        errors.dob2 = 'Invalid patient Date of birth format'
      }
    }
    if (Object.keys(errors).length > 0) {
      this.setState({ errors })
      const errorMessage = Object.values(errors)[0]
      if (typeof errorMessage === 'string') {
        handleErrorMessageAlert(errorMessage)
      }
    }
    return errors
  }

  editEstimate = () => {
    const stateForm: any = { ...this.state.form }
    const errors = this.validate()
    if (Object.keys(errors).length > 0) {
      return
    }
    const form = {
      appointmentIdSet: stateForm.appointmentIdSet && [stateForm.appointmentIdSet.value],
      cpt4IdSet: stateForm.cpt4IdSet && [stateForm.cpt4IdSet.value],
      firstName: stateForm.firstName,
      lastName: stateForm.lastName,
      middleInitial: stateForm.middleInitial,
      memberId: stateForm.memberId,
      serviceCode: stateForm.serviceCode,
      dob: new Date(stateForm.dob).toISOString(),
      gender: stateForm.gender ? stateForm.gender.value : null,
      relationship: stateForm.relationship ? stateForm.relationship.value : null,
      physicianId: stateForm.physician ? stateForm.physician.value : null,
      dependent: {
        dob: stateForm.dependent.dob ? new Date(stateForm.dependent.dob).toISOString() : null,
        firstName: stateForm.dependent.firstName,
        middleInitial: stateForm.dependent.middleInitial,
        lastName: stateForm.dependent.lastName,
        gender: stateForm.dependent.gender ? stateForm.dependent.gender.value : null
      },
      eligibilityDate: new Date().toISOString(),
      insuranceCarrierId: stateForm.payer ? stateForm.payer.value : null,
      estimationType: "OutPatientComplex",
      dependentSearch: stateForm.dependent.dob ? true : false
    }
    this.props.createEstimate(form)
  }

  onCancel = () => {
    history.push({
      pathname: routePath.ESTIMATES_PAGE,
      state: { from: this.props.location }
    })
  }

  render() {
    const { 
      patients, 
      relationships, 
      genders, 
      appointments, 
      physicians, 
      cpt4Codes 
    } = this.props
    return (
      <WrapBlock>
        <HeadTitle>
          <Title>Edit estimate</Title>
        </HeadTitle>
        <EstimateForm
          // @ts-ignore
          form={this.state.form}
          patients={patients}
          relationships={relationships}
          genders={genders}
          appointments={appointments}
          physicians={physicians}
          cpt4Codes={cpt4Codes}
          fetchAppointments={fetchAppointments}
          onSubmit={this.editEstimate}
          onCancel={() => history.push(routePath.ESTIMATES_PAGE)}
          onChangeInput={this.onChangeInput}
          errors={this.state.errors}
        />
      </WrapBlock>
    )
  }
}

// tslint:disable-next-line:no-empty-interface
interface ReceivedProps {}

type StateProps = ReturnType<typeof mapStateToProps>
type DispatchProps = ReturnType<typeof mapDispatchToProps>
type Props = StateProps &
  DispatchProps &
  ReceivedProps &
  RouteComponentProps<any>

const mapStateToProps = (state: AppState) => ({
  isAccountCreated: state.auth.registrationData,
  relationships: state.references.relationships.data.map(item => ({
    value: item,
    label: camelCaseToSentence(item)
  })),
  genders: state.references.genders.data.map(item => ({
    value: item,
    label: camelCaseToSentence(item)
  })),
  appointments: state.references.appointments.data.content.map(item => ({
    value: item.id,
    label: item.description
  })),
  patients: state.references.patients.data.content.map(item => ({
    value: item.id,
    label: item.carrierName
  })),
  physicians: state.references.physicians.data.content.map(item => ({
    value: item.id,
    label: item.name
  })),
  estimate: state.estimates.estimate,
  cpt4Codes: state.references.cpt4Codes.data.content.map(item => ({
    value: item.id,
    label: item.cpt4Code
  }))
})

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      fetchAppointments,
      fetchRelationships,
      fetchGenders,
      fetchPatients,
      fecthEstimateById,
      fetchPhysicians,
      createEstimate,
      fetchCpt4Codes
    },
    dispatch
  )

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EstimateCreatePage)

const findInDictionary = (dictionary: Array<any>, value: number) => {
  return dictionary.find(
    d => d.value === value
  )
}

const WrapBlock = styled.div``

const Title = styled.h1`
  font-size: 30px;
  font-weight: 600;
  color: #152735;
  text-align: center;
  padding: 0;
`

const HeadTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2.5rem;
`

